/* eslint-disable @typescript-eslint/restrict-template-expressions */
import './JobQueue.scss';

import FilterListIcon from '@mui/icons-material/FilterList';
import { TablePagination, Typography } from '@mui/material';
import _ from 'lodash';
import debouce from 'lodash.debounce';
import AddAndEditJob from 'pages/common/AddAndEditJob';
import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { getAllListData } from 'services/CommonServices';
import { useAppSelector } from 'store/hooks';
import { jobFilterData } from 'store/reducers/UserReducer';
import {
  USER_CUSTOMER_DATA,
  USER_JOB_FILTER_DATA,
  USER_JOB_PAGINATION_DATA,
  USER_JOB_TAB_DATA,
  USER_RELOAD,
} from 'store/types/UserTypes';
import { MASTER_ENDPOINT } from 'utils/Constant';

import JobQueueFilter from './filter';
import JobQueueHeader from './JobQueueHeader';
import JobQueueTable from './JobQueueTable';
import { User } from './utils';

const JobQueue: React.FC = () => {
  // Helper to map Redux jobTab number → local tab id string
  const tabIdFromJobTab = (num: number): string => {
    if (num === 1) return 'my';
    if (num === 10) return 'overdue';
    if (num === 11) return 'closed';
    return 'all';
  };
  const {
    userInfo,
    reload,
    customerInfo,
    customerFilter,
    companyFilter,
    jobTab,
    jobFilter,
    jobPagination,
  }: any = useAppSelector((store: any) => store.userLogin);
  const [activeTab, setActiveTab] = useState<string>(() =>
    tabIdFromJobTab(jobTab ?? 0),
  );
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const location = useLocation();
  // Use Redux pagination if available, otherwise fallback to location state or defaults
  const initialSkip = jobPagination?.skip ?? location?.state?.Params?.skip ?? 0;
  const initialTake =
    jobPagination?.take ?? location?.state?.Params?.take ?? 50;
  const staticParam = {
    createdFrom: jobFilter.fromDate || '',
    createdTo: jobFilter.toDate || '',
    order: 'desc',
    reload: true,
    score: jobFilter.score || '',
    skip: initialSkip,
    sortby: '',
    take: initialTake,
  };
  const dispatch = useDispatch();
  const [openfilter, setOpenFilter] = useState(false);
  const [tabSearch, setTabSearch] = React.useState<string>('1');
  const [workStatusList, setWorkStatusList] = React.useState<any>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = React.useState<any>(true);
  const [params, setParams] = React.useState<any>({});
  const [tableInfo, setTableInfo] = React.useState<any>({
    list: [],
    total: 0,
  });
  const [openAddJob, setOpenAddJob] = useState(false);

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    setTabFun(
      tab === 'all' ? 0 : tab === 'my' ? 1 : tab === 'overdue' ? 10 : 11,
    );
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (String(e?.target?.value).startsWith(' ')) {
      return;
    } else {
      const newTake = params.take || 50;
      setParams((prev) => ({
        ...prev,
        reload: !prev.reload,
        search: e.target.value.trimStart(),
        skip: 0,
        take: newTake,
      }));
      // Save pagination to Redux (reset to page 1 on search)
      dispatch({
        payload: { skip: 0, take: newTake },
        type: USER_JOB_PAGINATION_DATA,
      });
    }
  };

  const debouncedResults = useMemo(() => {
    return debouce(handleChange, 1000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getSkillAndCustomer = (customerStatus: boolean) => {
    getAllListData(
      MASTER_ENDPOINT.Customer +
        `?status=true&order=asc&sortby=name${
          customerStatus
            ? `&publish=${String(
                jobTab === 3 ? '' : jobTab !== 4 ? true : false,
              )}&jobStatus=${
                [1].includes(jobTab)
                  ? 'published'
                  : jobTab === 2
                  ? 'matched,filled,lost,others'
                  : jobTab === 4
                  ? 'draft'
                  : ''
              }&jobUserId=${String(
                userInfo.roleId === 5 ? (jobTab !== 0 ? userInfo.id : '') : '',
              )}&deletedStatus=${jobTab === 3 ? 'true' : 'false'}`
            : ''
        }`,
    ).then((resp: any) => {
      if (!_.isEqual(customerInfo, resp?.data)) {
        dispatch({
          payload: resp.data,
          type: USER_CUSTOMER_DATA,
        });
      }
    });
  };
  const handleAddJob = () => {
    getSkillAndCustomer(false);
    setOpenAddJob(true);
  };

  const handleSortClick = () => {
    // console.log('Sort clicked');
    // Implement sort functionality
  };

  const handleRowSelect = (id: string) => {
    setSelectedRows((prev) => {
      if (prev.includes(id)) {
        return prev.filter((rowId) => rowId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      // Select all visible rows (you might want to implement select all across all pages)
      setSelectedRows([]);
    } else {
      setSelectedRows([]);
    }
  };

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    const newSkip = newPage * Number(params.take);
    setParams((prev) => ({
      ...prev,
      skip: newSkip,
    }));
    // Save pagination to Redux
    dispatch({
      payload: { skip: newSkip, take: Number(params.take) },
      type: USER_JOB_PAGINATION_DATA,
    });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const newTake = parseInt(event.target.value, 10);
    setParams((prev) => ({
      ...prev,
      skip: 0,
      take: newTake,
    }));
    // Save pagination to Redux
    dispatch({
      payload: { skip: 0, take: newTake },
      type: USER_JOB_PAGINATION_DATA,
    });
  };

  const tabs = [
    { id: 'all', label: 'All Jobs' },
    { id: 'my', label: 'My Job' },
    { id: 'overdue', label: 'Overdue' },
    { id: 'closed', label: 'Closed' },
  ];
  const setTabFun = React.useCallback(
    (num: any) => {
      dispatch({
        payload: num,
        type: USER_JOB_TAB_DATA,
      });
    },
    [dispatch],
  );
  // Check if any filters (including fromDate/toDate) are applied
  const hasActiveFilters = React.useMemo(() => {
    if (!jobFilter) {
      return false;
    }

    // Helper function to get date string in YYYY-MM-DD format
    const getDateString = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    // Calculate default dates (last 7 days) dynamically
    const today = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(today.getDate() - 7);
    const defaultFromDate = getDateString(sevenDaysAgo);
    const defaultToDate = getDateString(today);

    // Check if dates are different from default
    const hasCustomDates =
      (jobFilter.fromDate && jobFilter.fromDate !== defaultFromDate) ||
      (jobFilter.toDate && jobFilter.toDate !== defaultToDate);

    // Check if any non-date filters are applied
    const hasLocation = jobFilter.location && jobFilter.location.trim() !== '';
    const hasTitle = jobFilter.title && jobFilter.title.trim() !== '';
    const hasWorkStatus = jobFilter.workStatus && jobFilter.workStatus !== '';
    const hasAssignedTo = jobFilter.assignedTo && jobFilter.assignedTo !== '';
    const hasAssignedBy = jobFilter.assignedBy && jobFilter.assignedBy !== '';
    const hasSkills = jobFilter.skillData && jobFilter.skillData.length > 0;
    const hasPayRange =
      (jobFilter.payRangeFrom && jobFilter.payRangeFrom !== '') ||
      (jobFilter.payRangeTo && jobFilter.payRangeTo !== '');
    const hasSalary =
      (jobFilter.salaryFrom && jobFilter.salaryFrom !== 0) ||
      (jobFilter.salaryTo && jobFilter.salaryTo !== 0);
    const hasWorkLocation =
      jobFilter.workLocation && jobFilter.workLocation !== '';

    return (
      hasCustomDates ||
      hasLocation ||
      hasTitle ||
      hasWorkStatus ||
      hasAssignedTo ||
      hasAssignedBy ||
      hasSkills ||
      hasPayRange ||
      hasSalary ||
      hasWorkLocation
    );
  }, [jobFilter]);

  const resetFilter = React.useCallback(() => {
    // Helper function to get date string in YYYY-MM-DD format
    const getDateString = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    // Calculate default dates (last 7 days) dynamically
    const today = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(today.getDate() - 7);

    const resetFilterData = {
      ...jobFilterData,
      applyFilter: true,
      fromDate: getDateString(sevenDaysAgo),
      toDate: getDateString(today),
    };

    if (!_.isEqual(resetFilterData, jobFilter)) {
      dispatch({
        type: USER_RELOAD,
      });
      jobFilter.skillData = [];
      dispatch({
        payload: resetFilterData,
        type: USER_JOB_FILTER_DATA,
      });
    }
    setOpenFilter(false);
  }, [dispatch, jobFilter]);

  const handleClose1 = () => {
    setOpenAddJob(false);
  };
  const getWorkStatus = () => {
    getAllListData(MASTER_ENDPOINT.WorkStatus + '?status=true').then(
      (resp: any) => {
        setWorkStatusList(resp?.data);
      },
    );
  };

  console.log(jobTab);

  const userData = () => {
    getAllListData(
      `${MASTER_ENDPOINT.Admins}?sortby=name&order=ASC&roleId=5&search=&skip=&take=&organizationId=&status=true`,
    )
      .then((res) => {
        setUsers(res?.data);
      })
      .catch((err) => {
        // console.log({ err });
      });
  };
  const getJobData = async () => {
    setLoading(true);
    setTableInfo({
      list: [],
      total: 0,
    });
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    let url = `${MASTER_ENDPOINT.Jobs}?id=&order=${String(
      params.order,
    )}&sortby=${String(params.sortby)}&skip=${String(
      params.skip,
    )}&take=${String(params.take)}&search=${
      params.search ? String(params.search) : ''
    }&publish=${String(
      jobTab === 3 ? '' : jobTab !== 4 ? true : false,
    )}&status=${
      [1].includes(jobTab)
        ? 'published'
        : jobTab === 2
        ? 'matched,filled,lost,others'
        : jobTab === 4
        ? 'draft'
        : ''
    }&userId=${String(
      userInfo.roleId === 5 ? ([1].includes(jobTab) ? userInfo.id : '') : '',
    )}&deletedStatus=${jobTab === 3 ? 'true' : ''}&timezone=${timezone}`;
    if (jobFilter.applyFilter) {
      let skillId = '';
      if (jobFilter?.skillData && jobFilter?.skillData?.length) {
        jobFilter?.skillData.map((item: any) => {
          skillId += item?.id + ',';
        });
      }
      if (skillId) {
        skillId = skillId.replace(/,\s*$/, '');
      }
      url += `&workType=${jobFilter?.workLocation}&jobTitle=${
        jobFilter?.title
      }&location=${jobFilter?.location}&workStatus=${jobFilter?.workStatus}${
        tabSearch !== '1'
          ? `&salaryFrom=${jobFilter?.salaryFrom}&salaryTo=${jobFilter?.salaryTo}`
          : `&payRangeFrom=${
              jobFilter.payRangeFrom ? parseInt(jobFilter.payRangeFrom) : ''
            }&payRangeTo=${
              jobFilter.payRangeTo ? parseInt(jobFilter.payRangeTo) : ''
            }`
      }&skillId=${skillId}&assignedTo=${jobFilter?.assignedTo}&assignedBy=${
        jobFilter?.assignedBy
      }&isApply=true&createdFrom=${jobFilter?.fromDate || ''}&createdTo=${
        jobFilter?.toDate || ''
      }&score=${jobFilter?.score || ''}&sourceQueue=${'benchQueue'}`;
    }
    if (customerFilter && customerFilter !== 'all') {
      url += '&customerId=' + customerFilter;
    }
    if (jobTab === 0) {
      url += `&organizationId=${String(companyFilter)}&screen=all`;
    }
    if ([10, 11].includes(jobTab)) {
      url += `&screen=${jobTab === 10 ? 'overdue' : 'closed'}`;
    }
    await getAllListData(url)
      .then((resp) => {
        setLoading(false);
        setTableInfo({
          list: resp.data,
          total: resp?.total,
        });
      })
      .catch((err: any) => {
        setLoading(false);
      });
  };
  React.useEffect(() => {
    if (Object.keys(params).length) {
      getJobData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  React.useEffect(() => {
    // When filters or tabs change, preserve pagination from Redux
    const preservedSkip = jobPagination?.skip ?? 0;
    const preservedTake = jobPagination?.take ?? 50;
    const preservedSearch = params.search || '';
    if (Object.keys(params).length) {
      setParams({
        ...staticParam,
        reload: !params.reload,
        search: preservedSearch,
        skip: preservedSkip,
        take: preservedTake,
      });
    } else {
      setParams({
        ...staticParam,
        search: preservedSearch,
        skip: preservedSkip,
        take: preservedTake,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    jobTab,
    reload,
    customerFilter,
    companyFilter,
    jobFilter.fromDate,
    jobFilter.toDate,
  ]);

  React.useEffect(() => {
    setActiveTab(tabIdFromJobTab(jobTab ?? 0));
  }, [jobTab]);

  React.useEffect(() => {
    getWorkStatus();
    userData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (openfilter) {
      getWorkStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openfilter]);
  // Removed cleanup effect that was resetting filters on unmount
  // Filters (fromDate, toDate) should persist in Redux when navigating away
  // If tab reset is needed, it can be handled separately
  // React.useEffect(() => {
  //   return () => {
  //     resetFilter(); // This was resetting filters on every navigation
  //     setTabFun(0);
  //   };
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []);

  return (
    <Typography component={'div'} className="job-queue-container">
      {/* Header Component */}
      <JobQueueHeader
        onSearchChange={debouncedResults}
        onAddJob={handleAddJob}
        onSortClick={handleSortClick}
      />

      {/* Table Component */}
      <div className="job-queue-table-container">
        <div className="job-queue-table-container-content">
          <div className="job-queue-table-container-tabs">
            {/* Tabs */}
            <div className="job-queue-tabs">
              {tabs.map((tab) => (
                <Typography
                  key={tab.id}
                  className={`job-queue-tab ${
                    activeTab === tab.id ? 'active' : ''
                  }`}
                  onClick={() => handleTabChange(tab.id)}
                  sx={{
                    color: '#656565 !important',
                    cursor: 'pointer',

                    display: 'inline-block',

                    fontFamily: 'Red Hat Display Medium !important',
                    // fontFamily: 'Red Hat Display Regular',
                    fontSize: '13px',
                    fontWeight: 'medium',
                    marginRight: '8px',
                    padding: '8px 16px',
                    textDecoration: 'none',
                  }}>
                  {tab.label}
                </Typography>
              ))}
            </div>
            <JobQueueTable
              data={tableInfo?.list || []}
              selectedRows={selectedRows}
              onRowSelect={handleRowSelect}
              onSelectAll={handleSelectAll}
              loading={loading}
              users={users}
              params={params}
            />
          </div>

          <div>
            <div
              className="job-queue-table-container-filters"
              onClick={() => setOpenFilter(true)}>
              <div style={{ display: 'inline-block', position: 'relative' }}>
                <FilterListIcon />
                {hasActiveFilters && (
                  <div
                    style={{
                      backgroundColor: '#EF0003',
                      border: '1.5px solid #ffffff',
                      borderRadius: '50%',
                      height: '8px',
                      position: 'absolute',
                      right: '2px',
                      top: '2px',
                      width: '8px',
                      zIndex: 1,
                    }}
                  />
                )}
              </div>
              <Typography
                component="div"
                sx={{
                  color: '#181D1F',
                  fontFamily: 'Red Hat Display Regular',
                  fontSize: '13px',
                  fontWeight: 400,
                  rotate: '90deg',
                }}>
                Filters
              </Typography>
            </div>
          </div>
        </div>

        {/* Pagination Component */}
        <div className="job-queue-pagination-container">
          <TablePagination
            sx={{
              '& .MuiTablePagination-select': {
                fontFamily: 'Red Hat Display Medium !important',
              },
              '& p': {
                fontFamily: 'Red Hat Display Medium !important',
              },
            }}
            component="div"
            count={tableInfo?.total || 0}
            page={Number(params.skip) / Number(params.take)}
            onPageChange={handlePageChange}
            rowsPerPage={params.take ? parseInt(params.take) : 0}
            rowsPerPageOptions={[50, 100, 150, 200, 250]}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </div>
      </div>

      {/* Filters Component */}
      {
        <JobQueueFilter
          openfilter={openfilter}
          setOpenFilter={setOpenFilter}
          tabSearch={tabSearch}
          jobFilter={jobFilter}
          workStatusList={workStatusList}
          users={users}
          setTabSearch={setTabSearch}
          resetFilter={resetFilter}
        />
      }

      {/* Add Job Modal */}
      {openAddJob && (
        <AddAndEditJob
          workStatusList={workStatusList}
          open={openAddJob}
          editId={null}
          editRow={null}
          scroll={'body'}
          onClose={handleClose1}
        />
      )}
    </Typography>
  );
};

export default JobQueue;
